import type { AssessmentCycle, CommitmentsResponse } from '../../../api/types';
import { parseDate } from './format';

/**
 * 1-based position (oldest first) of the student's most recent cycle that is still open, for the
 * "Remediation · cycle N" tag. Null when no cycle is open.
 */
export function remediationCycleNumber(cycles: Pick<AssessmentCycle, 'id' | 'status' | 'created_at'>[] | undefined): number | null {
  if (!cycles || cycles.length === 0) return null;
  const ordered = [...cycles].sort((a, b) => (parseDate(a.created_at)?.getTime() ?? 0) - (parseDate(b.created_at)?.getTime() ?? 0));
  for (let i = ordered.length - 1; i >= 0; i -= 1) {
    if (ordered[i].status !== 'closed') return i + 1;
  }
  return null;
}

type ActiveCommitment = CommitmentsResponse['active'][number];

/**
 * "Behind" when the share of the target reached is below the share of the time elapsed between the
 * commitment's start and its deadline; otherwise "On track". Null when the dates or target are unusable.
 */
export function commitmentPace(c: ActiveCommitment, now: Date = new Date()): 'behind' | 'on_track' | null {
  const start = parseDate(c.started_at);
  const end = parseDate(c.deadline);
  if (!start || !end || c.target_value <= 0 || end.getTime() <= start.getTime()) return null;
  const elapsed = Math.min(1, Math.max(0, (now.getTime() - start.getTime()) / (end.getTime() - start.getTime())));
  const progress = Math.min(1, Math.max(0, (c.last_progress_value ?? 0) / c.target_value));
  return progress + 1e-9 < elapsed ? 'behind' : 'on_track';
}
